import React from "react";
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Flex,
  Text,
  ModalFooter,
  HStack,
  Box,
} from "@chakra-ui/react";
import { useUIContext } from "context/UIContext";

interface CustomModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "full";
  maxWidth?: string;
  maxHeight?: string;
  bodyMaxHeight?: string;
  isCentered?: boolean;
  /** Shift modal right so it centers in the main content area beside the sidebar. */
  centerInMainContent?: boolean;
  motionPreset?: "slideInBottom" | "slideInRight" | "scale" | "none";
  showCloseButton?: boolean;
  overlayBg?: string;
  footer?: React.ReactNode;
  headerAction?: React.ReactNode;
}

const CustomModal: React.FC<CustomModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = "md",
  maxWidth = "90vw",
  maxHeight = "90vh",
  bodyMaxHeight,
  isCentered = true,
  centerInMainContent = false,
  motionPreset = "scale",
  showCloseButton = true,
  overlayBg = "blackAlpha.300",
  footer,
  headerAction,
}) => {
  const { isNavbarCollapsed } = useUIContext();
  const mainContentCenterOffset = isNavbarCollapsed ? "30px" : "120px";

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered={isCentered} motionPreset={motionPreset} size={size}>
      <ModalOverlay bg={overlayBg} />
      <ModalContent
        m="5%"
        ml={centerInMainContent ? { base: "5%", md: mainContentCenterOffset } : "5%"}
        maxW={maxWidth || (size === "full" ? "90vw" : undefined)}
        maxH={maxHeight || (size === "full" ? "90vh" : undefined)}
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>
          <Flex direction="row" justify="space-between" align="center" width="100%">
            <Flex direction="column" justify="center" flex="1" p={1} minW={0}>
              {typeof title === "string" ? (
                <Text fontSize="lg" fontWeight="bold" color="black" mb={0}>
                  {title}
                </Text>
              ) : (
                <Box minW={0}>{title}</Box>
              )}
            </Flex>
            <HStack>
              {headerAction && (
                <Flex align="center" mr={showCloseButton ? 14 : 0}>
                  {headerAction}
                </Flex>
              )}
              {showCloseButton && <ModalCloseButton m={1} />}
            </HStack>
          </Flex>
        </ModalHeader>
        <ModalBody overflowY="auto" maxH={bodyMaxHeight || maxHeight} flex="1" minH={0}>
          {children}
        </ModalBody>
        {footer && <ModalFooter flexShrink={0}>{footer}</ModalFooter>}
      </ModalContent>
    </Modal>
  );
};

export default CustomModal;
